import { noop } from 'lodash';
import { ReactElement, useMemo } from 'react';

import {
   EntitlementStatementSimple,
   EntitlementStatementSimpleQuery,
   Patient,
} from '@dreryk/egabinet-patient-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { dateFormatter, Ordering, useTranslate } from '@dreryk/react-common';
import { createDynamicColumnsWidth, TableColumnConfiguration, ValueRendererProps } from '@dreryk/react-components';

import { entitlementStatementsTranslationKeys } from '../../translations';

import { ActionsCell } from './cell/ActionsCell';
import { IdentityDocumentCell } from './cell/IdentityDocumentCell';

const DEFAULT_ORDERINGS: Ordering<EntitlementStatementSimple>[] = [
   {
      descending: true,
      property: 'submissionDate',
   },
];

type EntitlementStatementsQueryParameters = {
   patientId: Patient['id'];
};

export const useEntitlementStatementsDataConfiguration = (
   parameters: EntitlementStatementsQueryParameters
): LazyLoadedDataConfigurationByCqrsQuery<EntitlementStatementSimple> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<EntitlementStatementSimple>(() => {
      const query = new EntitlementStatementSimpleQuery();
      query.patientId = parameters.patientId;
      return query;
   }, DEFAULT_ORDERINGS);
};

export const useEntitlementStatementsTableColumns = (
   onArchive: VoidFunction
): TableColumnConfiguration<EntitlementStatementSimple>[] => {
   const translate = useTranslate();
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         documentType: '100%',
         identityDocument: '330px',
         submissionDate: '180px',
         effectiveDate: '180px',
         options: '64px',
      });

      return [
         {
            name: 'entitlementStatementsTypeOfDocument',
            accessor: () => `${translate(entitlementStatementsTranslationKeys.entitlementStatement)}`,
            label: entitlementStatementsTranslationKeys.typeOfDocument,
            width: columnsWidth.documentType,
            sortingDisabled: true,
         },
         {
            name: 'entitlementStatementsIdentityDocument',
            label: entitlementStatementsTranslationKeys.identityDocument,
            accessor: noop,
            width: columnsWidth.identityDocument,
            renderer: (props: ValueRendererProps<EntitlementStatementSimple>): ReactElement | null => {
               const {
                  patientIdentityDocumentType,
                  patientIdentityDocumentValue,
                  representativeIdentityDocumentType,
                  representativeIdentityDocumentValue,
               } = props.bean;
               return (
                  <IdentityDocumentCell
                     patientIdentityDocumentType={patientIdentityDocumentType}
                     patientIdentityDocumentValue={patientIdentityDocumentValue}
                     representativeIdentityDocumentType={representativeIdentityDocumentType}
                     representativeIdentityDocumentValue={representativeIdentityDocumentValue}
                  />
               );
            },
            sortingDisabled: true,
         },
         {
            name: 'submissionDate',
            accessor: (entitlementStatement) => entitlementStatement.submissionDate,
            label: entitlementStatementsTranslationKeys.submissionDate,
            width: columnsWidth.submissionDate,
            formatter: dateFormatter,
         },
         {
            name: 'effectiveDate',
            accessor: (entitlementStatement) => entitlementStatement.effectiveDate,
            label: entitlementStatementsTranslationKeys.effectiveDate,
            width: columnsWidth.effectiveDate,
            formatter: dateFormatter,
         },
         {
            name: 'actions',
            label: '',
            width: columnsWidth.options,
            accessor: noop,
            renderer: ({ bean }: ValueRendererProps<EntitlementStatementSimple>) => {
               return <ActionsCell id={bean.id} onArchive={onArchive} />;
            },
            sortingDisabled: true,
         },
      ];
   }, [onArchive, translate]);
};
